import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { TopBar } from "./TopBar";
import { NavDropdown, MobileNavGroup } from "./NavDropdown";
import { useI18n } from "../i18n/I18nContext";
import { GHL_CALENDAR_URL, externalLinkProps } from "../config/ghl";

export const Navbar = () => {
  const { t, lang, setLang, prefix, lp } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname, loc.hash]);

  const links = [
    { to: prefix || "/", label: t.nav.home, testid: "nav-home", end: true },
    { to: lp("/services"), label: t.nav.services, testid: "nav-services", menu: t.navMenus.services },
    { to: lp("/how-it-works"), label: t.nav.how, testid: "nav-how" },
    { to: lp("/pricing"), label: t.nav.pricing, testid: "nav-pricing" },
    { to: lp("/industries"), label: t.nav.industries, testid: "nav-industries", menu: t.navMenus.industries },
    { to: lp("/about"), label: t.nav.about, testid: "nav-about" },
    { to: lp("/contact"), label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#060B19]/85 transition-colors ${scrolled || open ? "border-b border-white/10" : "border-b border-transparent"}`}
      data-testid="site-navbar"
    >
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to={prefix || "/"} data-testid="nav-logo-link"><Logo hideWordmarkOnMobile /></Link>

        <nav className="hidden xl:flex items-center gap-1">
          {links.map((l) =>
            l.menu ? (
              <NavDropdown key={l.to} label={l.label} to={l.to} items={l.menu} testid={l.testid} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                end={!!l.end}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? "text-white bg-white/5" : "text-slate-300 hover:text-white hover:bg-white/5"}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-1 text-xs font-mono bg-white/5 border border-white/10 rounded-full p-1">
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 rounded-full transition-colors ${lang === "en" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
            >EN</button>
            <button
              data-testid="lang-nl"
              onClick={() => setLang("nl")}
              className={`px-2.5 py-1 rounded-full transition-colors ${lang === "nl" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
            >NL</button>
          </div>

          <a
            href={GHL_CALENDAR_URL}
            {...externalLinkProps}
            data-testid="nav-book-demo-btn"
            className="btn-cta hidden md:inline-flex items-center text-sm font-semibold px-4 py-2 rounded-lg"
          >{t.nav.bookDemo}</a>

          <button data-testid="mobile-menu-toggle" className="xl:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-white/10 bg-[#060B19]/95 backdrop-blur-xl px-6 py-5 flex flex-col gap-1 max-h-[calc(100vh-120px)] overflow-y-auto" data-testid="mobile-menu">
          {links.map((l) =>
            l.menu ? (
              <MobileNavGroup key={l.to} label={l.label} to={l.to} items={l.menu} testid={l.testid} onNavigate={() => setOpen(false)} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                end={!!l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `px-3 py-2.5 rounded-lg ${isActive ? "bg-white/10 text-white" : "text-slate-300"}`}
              >{l.label}</NavLink>
            )
          )}
          <a href={GHL_CALENDAR_URL} {...externalLinkProps} data-testid="mobile-book-demo-btn" className="btn-cta mt-2 text-center py-2.5 rounded-lg font-semibold">{t.nav.bookDemo}</a>
          <div className="flex gap-2 mt-2">
            <button data-testid="mobile-lang-en" onClick={() => setLang("en")} className={`flex-1 py-2 rounded-lg text-xs font-mono ${lang === "en" ? "bg-blue-600 text-white" : "bg-white/5 text-slate-300"}`}>EN</button>
            <button data-testid="mobile-lang-nl" onClick={() => setLang("nl")} className={`flex-1 py-2 rounded-lg text-xs font-mono ${lang === "nl" ? "bg-blue-600 text-white" : "bg-white/5 text-slate-300"}`}>NL</button>
          </div>
        </div>
      )}
    </header>
  );
};
